import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCar } from '@/contexts/CarContext';
import { useI18n } from '@/i18n/LanguageContext';
import { generateHealthReport, HealthReportData } from '@/services/healthReportGenerator';
import HealthReportView from '@/components/reports/HealthReportView';
import { useToast } from '@/hooks/use-toast';
import { Download, Share2, Loader2, X, Copy, Check } from 'lucide-react';
import * as Drawer from "vaul";
import { supabase } from '@/lib/supabase';
import { generatePDF } from '@/lib/generatePDF';

const HealthReportPage: React.FC = () => {
  const { carId: carIdParam } = useParams<{ carId?: string }>();
  const navigate = useNavigate();
  const { cars } = useCar();
  const { t, language: appLanguage } = useI18n();
  const { toast } = useToast();
  
  const [selectedCarIdState, setSelectedCarIdState] = useState<string>(carIdParam || '');
  const [reportData, setReportData] = useState<HealthReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [shareDrawerOpen, setShareDrawerOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const reportContainerRef = useRef<HTMLDivElement>(null);

  // Set selected car from param or first car
  useEffect(() => {
    if (carIdParam) {
      setSelectedCarIdState(carIdParam);
    } else if (cars.length > 0 && !selectedCarIdState) {
      setSelectedCarIdState(cars[0].id);
    }
  }, [carIdParam, cars, selectedCarIdState]);

  // Auto-generate report when carId is provided in URL
  useEffect(() => {
    if (carIdParam && !reportData && !generating && cars.length > 0) {
      handleGenerateReport(carIdParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carIdParam, cars.length]); // Only depend on carIdParam and cars.length to avoid infinite loops

  const handleGenerateReport = async (carId?: string) => {
    const targetCarId = carId || selectedCarIdState;
    if (!targetCarId) {
      toast({
        title: t('healthReport.selectCar') || 'Select a car',
        description: t('healthReport.selectCarDesc') || 'Please select a car to generate the report',
        variant: 'destructive',
      });
      return;
    }

    setGenerating(true);
    setShareLink(null); // Reset share link when generating new report
    try {
      const data = await generateHealthReport(targetCarId);
      setReportData(data);
      setLoading(false);
    } catch (error) {
      console.error('Error generating health report:', error);
      toast({
        title: t('healthReport.error') || 'Error',
        description: error instanceof Error ? error.message : t('healthReport.errorDesc') || 'Failed to generate health report',
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!reportContainerRef.current || !reportData) {
      toast({
        title: t('healthReport.error') || 'Error',
        description: t('healthReport.reportDataNotAvailable') || 'Report data is not available',
        variant: 'destructive',
      });
      return;
    }

    setGeneratingPDF(true);
    try {
      // Get car name for filename
      const carName = reportData.car.nickname || `${reportData.car.make} ${reportData.car.model}`;
      
      // Hide navigation and share button temporarily
      const navBar = document.querySelector('nav[class*="fixed bottom-0"]');
      const shareButton = document.querySelector('button[data-health-report-share]');
      const pageLayoutHeader = document.querySelector('header[class*="sticky"]') || 
                               document.querySelector('header');
      
      [navBar, shareButton, pageLayoutHeader].forEach((el) => {
        if (el) {
          (el as HTMLElement).style.display = 'none';
        }
      });

      // Add white background class for PDF generation
      const reportContent = reportContainerRef.current?.querySelector('#health-report-content');
      if (reportContent) {
        reportContent.classList.add('pdf-only-white');
      }

      // Small delay to ensure elements are hidden and class is applied
      await new Promise(resolve => setTimeout(resolve, 100));

      // Generate PDF
      await generatePDF({
        element: reportContainerRef.current,
        carName,
      });

      toast({
        title: t('healthReport.downloadPdf') || 'Download PDF',
        description: t('healthReport.pdfDownloadedSuccess') || 'PDF downloaded successfully',
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: t('healthReport.error') || 'Error',
        description: error instanceof Error ? error.message : (t('healthReport.pdfFailedDesc') || 'Failed to generate PDF. Please try again.'),
        variant: 'destructive',
      });
    } finally {
      // Remove white background class (restore gray background for web view)
      const reportContent = reportContainerRef.current?.querySelector('#health-report-content');
      if (reportContent) {
        reportContent.classList.remove('pdf-only-white');
      }

      // Restore navigation and share button
      const navBar = document.querySelector('nav[class*="fixed bottom-0"]');
      const shareButton = document.querySelector('button[data-health-report-share]');
      const pageLayoutHeader = document.querySelector('header[class*="sticky"]') || 
                               document.querySelector('header');
      
      [navBar, shareButton, pageLayoutHeader].forEach((el) => {
        if (el) {
          const htmlEl = el as HTMLElement;
          htmlEl.style.display = '';
        }
      });
      
      setGeneratingPDF(false);
    }
  };

  // Generate share link in the background when drawer opens
  const generateShareLink = async () => {
    if (!selectedCarIdState || shareLink) {
      return; // Already have a link or no car selected
    }

    setGeneratingLink(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        console.error('No session available for share link generation');
        return;
      }

      const response = await fetch(
        `${supabaseUrl}/functions/v1/generate-health-report-share-link`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': supabaseAnonKey,
          },
          body: JSON.stringify({ 
            carId: selectedCarIdState,
            // Use app i18n locale (Profile / DB preferred_language), not SettingsContext — they can diverge
            language: appLanguage,
            // expiresInHours defaults to 336 (2 weeks) in the Edge Function
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        const hint = result.details ? ` (${result.details})` : '';
        throw new Error((result.error || 'Failed to generate share link') + hint);
      }

      setShareLink(result.shareLink);
    } catch (error) {
      console.error('Error generating share link:', error);
      // Don't show toast here - will show when user tries to copy
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleShare = async () => {
    if (!shareLink) {
      toast({
        title: t('healthReport.error') || 'Error',
        description: t('healthReport.shareLinkNotReady') || 'Share link is not ready yet. Please try again in a moment.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Copy link to clipboard
      await navigator.clipboard.writeText(shareLink);
      
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000); // Reset after 2 seconds
      
      toast({
        title: t('healthReport.share') || 'Share',
        description: t('healthReport.shareLinkCopied') || 'Share link copied to clipboard!',
      });
    } catch (error) {
      console.error('Error copying share link:', error);
      toast({
        title: t('healthReport.error') || 'Error',
        description: t('healthReport.copyLinkFailed') || 'Failed to copy link to clipboard',
        variant: 'destructive',
      });
    }
  };

  // Generate share link when report is loaded (only if we don't already have one)
  useEffect(() => {
    if (reportData && selectedCarIdState && !shareLink && !generatingLink) {
      generateShareLink();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportData, selectedCarIdState]);

  const handlePrint = () => {
    window.print();
  };

  // If report is generated, show it
  if (reportData) {
    return (
      <PageLayout
        title={t('healthReport.title') || 'Health Report'}
        showBackButton
        onBack={() => {
          setReportData(null);
          setLoading(false);
        }}
        className="bg-[#f5f5f5]"
      >
        <div className="max-w-4xl mx-auto" ref={reportContainerRef}>
          {/* Report view */}
          <HealthReportView data={reportData} />
        </div>

        {/* Floating Share Button */}
        <button
          onClick={() => setShareDrawerOpen(true)}
          className="fixed bottom-20 right-4 z-[55] w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-colors print:hidden"
          style={{ backgroundColor: '#f2fe9e' }}
          data-health-report-share
          aria-label={t('healthReport.shareAriaLabel') || 'Share'}
        >
          <img src="/icons/Share.png" alt={t('healthReport.shareAriaLabel') || 'Share'} className="w-6 h-6" />
        </button>

        {/* Share Drawer */}
        <Drawer.Root open={shareDrawerOpen} onOpenChange={setShareDrawerOpen} dismissible>
          <Drawer.Portal>
            <Drawer.Overlay className="fixed inset-0 bg-black/40 z-[60]" />
            <Drawer.Content className="fixed left-0 right-0 bottom-0 rounded-t-2xl bg-white shadow-2xl z-[70]">
              <div className="relative pt-3 pb-2">
                <button
                  aria-label={t('healthReport.closeAriaLabel') || t('actions.close') || 'Close'}
                  onClick={() => setShareDrawerOpen(false)}
                  className="absolute -top-20 left-1/2 -translate-x-1/2 bg-white border border-gray-200 rounded-full w-10 h-10 flex items-center justify-center shadow-lg"
                >
                  <X className="text-gray-700" size={18} />
                </button>
                <div className="mx-auto h-1 w-12 rounded-full bg-gray-300" />
              </div>
              <div className="px-4 pb-4">
                <h3 className="text-lg font-semibold mb-4 text-center">
                  {t('healthReport.shareDrawerTitle') || t('healthReport.share') || 'Share Health Report'}
                </h3>
                <div className="space-y-3">
                  <button
                    onClick={() => {
                      handleDownloadPDF();
                      setShareDrawerOpen(false);
                    }}
                    disabled={generatingPDF}
                    className="w-full flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gray-100">
                      {generatingPDF ? (
                        <Loader2 className="w-6 h-6 text-gray-700 animate-spin" />
                      ) : (
                        <Download className="w-6 h-6 text-gray-700" />
                      )}
                    </div>
                    <span className="flex-1 text-left font-medium">
                      {generatingPDF 
                        ? (t('healthReport.generating') || 'Generating...')
                        : (t('healthReport.downloadPdf') || 'Download PDF')}
                    </span>
                  </button>
                  <button
                    onClick={async () => {
                      await handleShare();
                    }}
                    disabled={generatingLink || !shareLink}
                    className="w-full flex items-center gap-3 p-4 rounded-xl bg-lime hover:bg-[#d4e848] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/20">
                      {generatingLink ? (
                        <Loader2 className="w-6 h-6 text-black animate-spin" />
                      ) : linkCopied ? (
                        <Check className="w-6 h-6 text-green-700" />
                      ) : (
                        <Copy className="w-6 h-6 text-black" />
                      )}
                    </div>
                    <span className="flex-1 text-left font-medium text-black">
                      {generatingLink 
                        ? (t('healthReport.generatingLink') || 'Generating link...')
                        : linkCopied 
                        ? (t('healthReport.copied') || 'Copied') 
                        : (t('healthReport.copyLink') || 'Copy Link')}
                    </span>
                  </button>
                </div>
              </div>
              <div className="pb-[calc(env(safe-area-inset-bottom)+16px)]" />
            </Drawer.Content>
          </Drawer.Portal>
        </Drawer.Root>
      </PageLayout>
    );
  }

  // Show generation UI (only if no carId in URL - allows manual selection)
  // If carId is in URL, we auto-generate, so this UI is skipped
  if (carIdParam) {
    // Show loading state while generating
    return (
      <PageLayout
        title={t('healthReport.title') || 'Health Report'}
        showBackButton
        onBack={() => navigate(-1)}
      >
        <div className="max-w-4xl mx-auto p-6">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-soft-blue" />
              <p className="text-gray-600">{t('healthReport.generatingReport') || t('healthReport.generating') || 'Generating report...'}</p>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={t('healthReport.generate') || 'Generate Health Report'}
      showBackButton
      onBack={() => navigate(-1)}
    >
      <div className="max-w-2xl mx-auto p-6">
        <div className="space-y-6">
          {/* Car selection */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {t('healthReport.selectCar') || 'Select Car'}
            </label>
            <Select
              value={selectedCarIdState}
              onValueChange={setSelectedCarIdState}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('healthReport.selectCarPlaceholder') || 'Choose a car'} />
              </SelectTrigger>
              <SelectContent>
                {cars.map((car) => (
                  <SelectItem key={car.id} value={car.id}>
                    {car.nickname || `${car.brand} ${car.model} ${car.year}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              {t('healthReport.description') || 'Generate a comprehensive health report including service history, active issues, pending tasks, and fluid change history.'}
            </p>
          </div>

          {/* Generate button */}
          <Button
            onClick={() => handleGenerateReport()}
            disabled={!selectedCarIdState || generating}
            className="w-full bg-lime text-black hover:bg-[#d4e848]"
            size="lg"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('healthReport.generating') || 'Generating...'}
              </>
            ) : (
              t('healthReport.generateReport') || 'Generate Report'
            )}
          </Button>
        </div>
      </div>
    </PageLayout>
  );
};

export default HealthReportPage;

